import React, { useEffect, useMemo, useState } from "react";
import {
    BookOpen, LifeBuoy, MessageCircle, ExternalLink, Search, CircleHelp,
    ChevronDown, ChevronUp, Mail, Copy, Check, Clock3, Headphones, Activity,
    ChevronRight, TriangleAlert, CircleX, SearchX, RefreshCw,
} from "lucide-react";
import { Link } from "react-router-dom";
import DemoRequestModal from "@/components/DemoRequestModal";
import { http } from "@/lib/api";

/** A compact, Portal-native help center — the full marketing /support
 * page (dark hero, DemoRequestModal, etc.) is a public-facing surface
 * with its own chrome, not something to embed inside the Portal shell.
 * Reuses the one real, working contact mechanism (DemoRequestModal)
 * rather than inventing a ticketing system.
 *
 * Real-data-only surfaces here:
 *  - support email: support@moovbytes.com is already the real address
 *    used in backend error copy (routers/auth.py's suspended-workspace
 *    message), not invented for this page.
 *  - support hours / response time: no configured SLA exists anywhere
 *    in the backend, so those rows are omitted rather than inventing one.
 *  - system status: GET /system-status (new, routers/system_status.py)
 *    actually pings the database rather than hardcoding "operational"
 *    the way Login.jsx's footer still does.
 */
const SUPPORT_EMAIL = "support@moovbytes.com";

const FAQS = [
    {
        id: "new-device",
        question: "How do I set up a new device for migration?",
        summary: "Install MoovBytes Desktop or the appropriate Agent, sign in, and register the device.",
        answer: "Download MoovBytes Desktop (or the standalone Agent for a server/NAS) from the Downloads tab, install it, and sign in with your MoovBytes account. On first launch it activates the device against your license automatically, using up one of your plan's device slots.",
        articleUrl: "/docs#1-getting-started",
    },
    {
        id: "file-types",
        question: "What file types are supported?",
        summary: "PC, server, and network-attached storage (NAS) migrations are all supported.",
        answer: "MoovBytes migrates full filesystems agent-to-agent — PC-to-PC, server-to-server, and NAS/data-storage migrations — with SHA-256 verification and resumable transfers, rather than restricting by individual file type.",
        articleUrl: "/docs",
    },
    {
        id: "license-usage",
        question: "How do I check my license usage?",
        summary: "View your current license usage and device allocation.",
        answer: "Open the Licenses tab to see your plan, activated-device count against your plan's cap, and renewal details in one place.",
        articleUrl: "/portal/licenses",
    },
    {
        id: "migration-complete",
        question: "What happens after a migration is completed?",
        summary: "Understand what happens to your files and how to verify completion.",
        answer: "Completed migrations are verified with SHA-256 checksums on both ends. You can review job status, transferred data, and any errors from the Desktop app's job history.",
        articleUrl: "/docs",
    },
    {
        id: "invite-teammates",
        question: "How do I invite teammates?",
        summary: "Add and manage team members in your organization.",
        answer: "Open the Team tab, go to Pending Invites, and send an invite by email with the role you want them to have. They'll get a one-time activation link.",
        articleUrl: "/portal/team",
    },
];

const STATUS_META = {
    operational: {
        label: "All systems operational", desc: "All MoovBytes services are running normally.",
        icon: Activity, tone: "bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300",
        iconBg: "bg-[#ECFDF3] text-[#16A34A] dark:bg-emerald-500/10", dot: "bg-emerald-500",
    },
    degraded: {
        label: "Some services are experiencing degraded performance", desc: "We're aware and investigating.",
        icon: TriangleAlert, tone: "bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300",
        iconBg: "bg-amber-500/10 text-amber-600", dot: "bg-amber-500",
    },
    outage: {
        label: "Service disruption detected", desc: "A core service is currently unreachable.",
        icon: CircleX, tone: "bg-[hsl(var(--destructive))]/10 border-[hsl(var(--destructive))]/30 text-[hsl(var(--destructive))]",
        iconBg: "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]", dot: "bg-[hsl(var(--destructive))]",
    },
    unknown: {
        label: "System status unavailable", desc: "Couldn't reach the status check right now.",
        icon: Activity, tone: "bg-muted border-border text-muted-foreground",
        iconBg: "bg-muted text-muted-foreground", dot: "bg-muted-foreground",
    },
};

export default function PortalSupport() {
    const [contactOpen, setContactOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [openFaq, setOpenFaq] = useState(null);
    const [copied, setCopied] = useState(false);
    const [status, setStatus] = useState(null); // null = loading

    useEffect(() => {
        http.get("/system-status")
            .then(({ data }) => setStatus(data.status))
            .catch(() => setStatus("unknown"));
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return FAQS;
        return FAQS.filter((f) =>
            f.question.toLowerCase().includes(q) ||
            f.summary.toLowerCase().includes(q) ||
            (f.answer || "").toLowerCase().includes(q),
        );
    }, [query]);

    const copyEmail = async () => {
        try {
            await navigator.clipboard.writeText(SUPPORT_EMAIL);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard unavailable — link is still selectable text */ }
    };

    const meta = STATUS_META[status] || null;

    return (
        <div className="p-6 sm:p-9" data-testid="portal-support-page">
            <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        Help &amp; support
                    </div>
                    <h1 className="mt-1.5 font-display text-3xl font-bold tracking-tight">Support</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Get help with your MoovBytes account, migrations, and the Client Portal.
                    </p>
                </div>
                <div className="relative w-full sm:w-[400px]">
                    <Search size={17} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search help articles, guides, or topics…"
                        aria-label="Search support articles"
                        data-testid="portal-support-search"
                        className="h-10 w-full rounded-md border border-input bg-surface pl-9 pr-3 text-xs outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/10"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <SupportCard
                    icon={BookOpen}
                    title="User guide"
                    body="Step-by-step documentation for setting up devices, running migrations, and using the Client Portal."
                    action={<Link to="/docs" className="inline-flex items-center gap-1 text-brand hover:underline">Open user guide <ExternalLink size={11} /></Link>}
                />
                <SupportCard
                    icon={MessageCircle}
                    title="Contact support"
                    body="Reach the MoovBytes team directly for account or technical help."
                    action={
                        <button onClick={() => setContactOpen(true)} data-testid="portal-support-contact-btn" className="inline-flex items-center gap-1 text-brand hover:underline">
                            Contact us <ExternalLink size={11} />
                        </button>
                    }
                />
                <SupportCard
                    icon={LifeBuoy}
                    title="Resource center"
                    body="Guides, FAQs, use cases, and product resources."
                    action={
                        <a href="/support" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand hover:underline">
                            Visit resource center <ExternalLink size={11} />
                        </a>
                    }
                />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1.75fr_minmax(320px,0.8fr)]">
                {/* FAQ */}
                <div className="rounded-lg border border-border bg-surface">
                    <div className="flex items-start justify-between gap-3 border-b border-border p-4">
                        <div className="flex items-start gap-3">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[9px] bg-[#EEF4FF] text-brand dark:bg-brand/10">
                                <CircleHelp size={21} />
                            </div>
                            <div>
                                <h2 className="font-display text-[15px] font-bold">Frequently asked questions</h2>
                                <p className="mt-0.5 text-xs text-muted-foreground">Find quick answers to common questions about MoovBytes.</p>
                            </div>
                        </div>
                        <a href="/support" target="_blank" rel="noreferrer" className="shrink-0 whitespace-nowrap text-xs font-semibold text-brand hover:underline">
                            View all articles →
                        </a>
                    </div>

                    {filtered.length === 0 ? (
                        <div className="flex flex-col items-center gap-2 p-10 text-center">
                            <SearchX size={26} className="text-muted-foreground" />
                            <div className="text-sm font-semibold">No help articles found</div>
                            <p className="text-xs text-muted-foreground">Try another search term or contact the MoovBytes support team.</p>
                            <button onClick={() => setContactOpen(true)} className="mt-1 inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-xs font-semibold text-white hover:opacity-90">
                                Contact support
                            </button>
                        </div>
                    ) : (
                        <div data-testid="portal-support-faq-list">
                            {filtered.map((f) => {
                                const open = openFaq === f.id;
                                return (
                                    <div key={f.id} className="border-b border-border/60 last:border-b-0">
                                        <button
                                            onClick={() => setOpenFaq(open ? null : f.id)}
                                            aria-expanded={open}
                                            aria-controls={`faq-panel-${f.id}`}
                                            data-testid={`portal-support-faq-${f.id}`}
                                            className="flex w-full items-start justify-between gap-3 px-4 py-3.5 text-left hover:bg-muted/30"
                                        >
                                            <div>
                                                <div className="text-xs font-semibold">{f.question}</div>
                                                <div className="mt-1 text-[11px] text-muted-foreground">{f.summary}</div>
                                            </div>
                                            {open ? <ChevronUp size={15} className="mt-0.5 shrink-0 text-muted-foreground" /> : <ChevronDown size={15} className="mt-0.5 shrink-0 text-muted-foreground" />}
                                        </button>
                                        {open && (
                                            <div id={`faq-panel-${f.id}`} className="px-4 pb-4 text-[11.5px] leading-relaxed text-muted-foreground">
                                                {f.answer}
                                                {f.articleUrl && (
                                                    <div className="mt-2">
                                                        <Link to={f.articleUrl} className="font-semibold text-brand hover:underline">Read full guide →</Link>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Right column */}
                <div className="space-y-4">
                    <div className="rounded-lg border border-border bg-surface">
                        <div className="flex items-start justify-between gap-3 border-b border-border p-4">
                            <div className="flex items-start gap-3">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[9px] bg-[#EEF4FF] text-brand dark:bg-brand/10">
                                    <MessageCircle size={20} />
                                </div>
                                <div>
                                    <h2 className="font-display text-[14px] font-bold">Contact support</h2>
                                    <p className="mt-0.5 text-[11px] text-muted-foreground">Need additional help? Our team is here for you.</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setContactOpen(true)}
                                aria-label="Contact MoovBytes support"
                                className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-[11px] font-semibold text-white hover:opacity-90"
                            >
                                <Mail size={13} /> Contact support
                            </button>
                        </div>
                        <div className="flex items-center gap-3 border-t border-border/60 px-4 py-3">
                            <div className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-lg bg-muted/50">
                                <Mail size={17} className="text-muted-foreground" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-xs font-semibold">Email</div>
                                <div className="truncate text-[11px] text-muted-foreground">{SUPPORT_EMAIL}</div>
                            </div>
                            <button
                                onClick={copyEmail}
                                aria-label="Copy support email address"
                                title="Copy email address"
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                            >
                                {copied ? <Check size={15} /> : <Copy size={15} />}
                            </button>
                        </div>
                        {/* Support hours / response-time rows intentionally omitted —
                            no configured SLA exists in the backend for either, and
                            the redesign brief explicitly says not to invent one. */}
                    </div>

                    <div className="rounded-lg border border-border bg-surface p-4">
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-[9px] ${meta ? meta.iconBg : "bg-muted text-muted-foreground"}`}>
                                    <Activity size={20} />
                                </div>
                                <div>
                                    <h2 className="font-display text-[14px] font-bold">System status</h2>
                                    <p className="mt-0.5 text-[11px] text-muted-foreground">Check the current status of MoovBytes services.</p>
                                </div>
                            </div>
                            <a href="/support" target="_blank" rel="noreferrer" aria-label="View MoovBytes system status" className="shrink-0 whitespace-nowrap text-[11px] font-semibold text-brand hover:underline">
                                View status page ↗
                            </a>
                        </div>

                        <div className="mt-3">
                            {status === null ? (
                                <div className="flex items-center gap-2 rounded-md border border-border bg-muted/20 px-3 py-2.5 text-xs text-muted-foreground">
                                    <RefreshCw size={13} className="animate-spin" /> Checking status…
                                </div>
                            ) : (
                                <div className={`flex items-center justify-between gap-2 rounded-md border px-3.5 py-3 ${meta.tone}`} data-testid="portal-support-status-panel">
                                    <div className="flex items-center gap-2.5">
                                        <span className={`h-2 w-2 shrink-0 rounded-full ${meta.dot}`} />
                                        <div>
                                            <div className="text-xs font-semibold">{meta.label}</div>
                                            <div className="mt-0.5 text-[11px] opacity-90">{meta.desc}</div>
                                        </div>
                                    </div>
                                    <ChevronRight size={15} className="shrink-0 opacity-70" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <DemoRequestModal open={contactOpen} onClose={() => setContactOpen(false)} />
        </div>
    );
}

function SupportCard({ icon: Icon, title, body, action }) {
    return (
        <div className="rounded-lg border border-border bg-surface p-4 transition hover:border-foreground/20">
            <div className="flex items-start justify-between gap-2">
                <div className="flex h-12 w-12 items-center justify-center rounded-[9px] bg-[#EEF4FF] text-brand dark:bg-brand/10">
                    <Icon size={22} />
                </div>
                <ChevronRight size={16} className="mt-1 text-foreground/50" />
            </div>
            <div className="mt-3 font-display text-[14px] font-bold">{title}</div>
            <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">{body}</p>
            <div className="mt-3 text-[11.5px] font-semibold">{action}</div>
        </div>
    );
}
